import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import { TextInput } from "../../../../form/textInput";
import { operationType, selectDataType } from "../../../../../services/types";
import { updateNodeValue } from "../../../../../services/method";
import { Textarea } from "../../../../form/textarea";
import { Select } from "../../../../form/select";
import { Main, Styled } from "./style";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { ExpandedBlock } from "../../../expanded";

type FacebookPostingProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

type FacebookPostingRequest = "normal_post" | "photo_post" | "video_post";

function normalizeFacebookRequest(request?: string): FacebookPostingRequest {
  if (
    request === "normal_post" ||
    request === "photo_post" ||
    request === "video_post"
  )
    return request;
  return "normal_post";
}

export function FacebookPosting({
  id,
  data,
  setChildMinimize,
  setChildSettings,
}: FacebookPostingProps) {
  const [requests, setRequests] = React.useState<selectDataType[]>([
    { id: 1, name: "Normal post", refId: "normal_post", isActive: true },
    { id: 2, name: "Post include photo", refId: "photo_post" },
    { id: 3, name: "Post include video", refId: "video_post" },
  ]);
  const { setNodes } = useReactFlow();
  const [settings, setSettings] = React.useState<settingsDataType>({
    isToggle: false,
  });
  const [isExpanded, setIsExpanded] = React.useState(false);

  // --
  React.useEffect(() => {
    if (setChildMinimize)
      setChildMinimize(() => {
        setIsExpanded((prev) => !prev);
      });
    if (setChildSettings)
      setChildSettings(() => {
        setSettings((prev) => ({ ...prev, isToggle: !prev.isToggle }));
      });
  }, [setChildMinimize, setChildSettings]);

  // ---
  React.useEffect(() => {
    const request = normalizeFacebookRequest(data.value?.request);
    setRequests((prev) =>
      prev.map((item) => ({ ...item, isActive: item.refId === request })),
    );

    if (!data.value || data.value.request !== request) {
      updateNodeValue(id, setNodes, { request });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.value?.request, id, setNodes]);

  // ---
  const handleSelectRequest = (selectId: number) => {
    const refId = requests.find((item) => item.id === selectId)?.refId as
      | FacebookPostingRequest
      | undefined;
    if (!refId) return;

    setRequests((prev) =>
      prev.map((item) => ({
        ...item,
        isActive: item.id === selectId,
      })),
    );
    updateNodeValue(id, setNodes, { request: refId });
  };

  // ---
  const handleChangeInput = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    keyRef?: string,
  ) => {
    if (keyRef) updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
  };

  const request = normalizeFacebookRequest(data.value?.request);
  const isWarning = !data.value?.page_id || !data.value?.access_token;

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon">
              <IoWarningOutline size={21} />
            </span>
          </>
        ) : data.output ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon">
              <IoCheckmarkCircleOutline size={21} />
            </span>
          </>
        ) : null}

        {isExpanded ? (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: "420px" , maxWidth: "420px" }}>
            <Styled style={{ marginTop: "-13px" }}>
              <TextInput
                keyRef="access_token"
                value={data.value?.access_token}
                title="Token"
                placeholder="page_access_token"
                handleChange={handleChangeInput}
              />
              <TextInput
                keyRef="page_id"
                value={data.value?.page_id}
                title="Page ID"
                placeholder="facebook_page_id"
                handleChange={handleChangeInput}
              />
              <Select
                title="Type"
                data={requests}
                handleSelectItem={handleSelectRequest}
                isSmall={true}
              />

              {request === "normal_post" ? (
                <Textarea
                  keyRef="message"
                  value={data.value?.message}
                  placeholder="Write post message..."
                  handleChange={handleChangeInput}
                />
              ) : request === "photo_post" ? (
                <>
                  <TextInput
                    keyRef="url"
                    value={data.value?.url}
                    title="Photo"
                    placeholder="https://example.com/photo.png"
                    handleChange={handleChangeInput}
                  />
                  <Textarea
                    keyRef="caption"
                    value={data.value?.caption}
                    placeholder="Write photo caption..."
                    handleChange={handleChangeInput}
                  />
                </>
              ) : (
                <>
                  <TextInput
                    keyRef="file_url"
                    value={data.value?.file_url}
                    title="URL"
                    placeholder="https://example.com/video.mp4"
                    handleChange={handleChangeInput}
                  />
                  <Textarea
                    keyRef="description"
                    value={data.value?.description}
                    placeholder="Write video description..."
                    handleChange={handleChangeInput}
                  />
                </>
              )}
            </Styled>
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}